import { booleanPointOnLine } from '@turf/boolean-point-on-line';
import { lineString, type Position } from '@turf/helpers';
import isEqual from 'lodash.isequal';
import logError from '@alltrails/core/utils/logError';
import { CustomRouteSegment } from '../../types/CustomRoute';
import { LatLng } from '../../types/Geo';
import { reverseCoord } from '../legacyGeoJSONConversions';

export const deduplicateCoordinates = (points: Position[]): Position[] => {
  return points.reduce((acc, coord, index) => {
    if (index === 0 || coord[0] !== acc[acc.length - 1][0] || coord[1] !== acc[acc.length - 1][1]) {
      acc.push(coord);
    }

    return acc;
  }, [] as Position[]);
};

const splitPathAtMarker = (points: LatLng[], latitude: number, longitude: number): { beforePoints: LatLng[]; afterPoints: LatLng[] } => {
  const markerIndex = points.findIndex(point => point[1] === latitude && point[0] === longitude);
  const beforePoints = points.slice(0, markerIndex);
  const afterPoints = points.slice(markerIndex + 1);
  return { beforePoints, afterPoints };
};

const markerExistsOnMultiplePaths = (latitude: number, longitude: number, segment: CustomRouteSegment): boolean => {
  const duplicates = segment.paths.filter(path => isEqual(path.points[0], [latitude, longitude]));
  return duplicates.length > 1;
};

// Return the position of the marker if the point is found more than once on the segment.
// Top is the "back" portion of an overlapping portion of a segment, while bottom would be the "out" portion.
// For now, we are not concerned with overlapping points on different segments.
export const getMarkerPosition = (latitude: number, longitude: number, segment: CustomRouteSegment): 'top' | 'bottom' | 'both' | undefined => {
  if (markerExistsOnMultiplePaths(latitude, longitude, segment)) {
    return 'both';
  }

  // merge all paths into a single path
  const mergedPathPoints = deduplicateCoordinates(segment.paths.reduce((acc: LatLng[], path) => [...acc, ...path.points], [])).map(reverseCoord);

  // split the path at the marker's position, and remove duplicate points
  const { beforePoints, afterPoints } = splitPathAtMarker(mergedPathPoints as LatLng[], latitude, longitude);

  // if the point exists on the linestring before the marker, the marker is on the top line
  try {
    if (booleanPointOnLine([longitude, latitude], lineString(beforePoints), { epsilon: 5e-10 })) {
      return 'top';
    }
  } catch (error) {
    logError(new Error('Error when checking if point is on the back portion of an overlapping route segment', { cause: error }));
  }

  try {
    // if the point exists on the linestring after the marker, the marker is on the bottom line
    if (booleanPointOnLine([longitude, latitude], lineString(afterPoints), { epsilon: 5e-10 })) {
      return 'bottom';
    }
  } catch (error) {
    logError(new Error('Error when checking if point is on the out portion of an overlapping route segment', { cause: error }));
  }

  return undefined;
};
